import { oc } from "@orpc/contract";
import { z } from "zod";
import type { DeltaAnalysisResult } from "@erp/shared-types";

// ============================================================================
// Get Agreements for Supplier
// ============================================================================

export const GetAgreementsBySupplierRequestSchema = z.object({
  supplierId: z.string().uuid(),
});

export const GetAgreementsBySupplierResponseSchema = z.object({
  agreements: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      status: z.enum(["draft", "finalized"]),
    }),
  ),
});

export type GetAgreementsBySupplierRequest = z.infer<
  typeof GetAgreementsBySupplierRequestSchema
>;
export type GetAgreementsBySupplierResponse = z.infer<
  typeof GetAgreementsBySupplierResponseSchema
>;

// ============================================================================
// Get Agreement by ID (full detail with rules, product groups, documents)
// ============================================================================

const RebateProductGroupWithIdSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  supplier_id: z.string(),
});

const RebateTierWithIdSchema = z.object({
  id: z.string(),
  rebate_rule_id: z.string(),
  tier_order: z.number(),
  threshold_value: z.number(),
  rebate_value: z.number(),
});

const RebateRuleWithIdSchema = z.object({
  id: z.string(),
  rebate_agreement_id: z.string(),
  name: z.string(),
  is_retroactive_to_dollar_one: z.boolean().nullable(),
  threshold_type: z.string().nullable(),
  qualifying_inclusion_type: z.string().nullable().optional(),
  affected_inclusion_type: z.string().nullable().optional(),
  discountPercentage: z.string().optional(),
  qualifying_product_groups: z.array(RebateProductGroupWithIdSchema),
  affected_product_groups: z.array(RebateProductGroupWithIdSchema),
  tiers: z.array(RebateTierWithIdSchema),
  divisions: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  is_manual: z.boolean(),
});

const ClosureMetadataSchema = z.object({
  closedAt: z.string(),
  closedByUserId: z.string(),
  closedByUserName: z.string(),
  reason: z.enum(["immaterial", "settled", "fully_paid", "dispute", "other"]),
  notes: z.string().optional(),
  snapshotData: z.object({
    expectedAmount: z.number(),
    allocatedAmount: z.number(),
    unallocatedPayments: z.number(),
    writeOffAmount: z.number(),
  }),
});

const DeactivationMetadataSchema = z.object({
  deactivatedAt: z.string(),
  deactivatedByUserId: z.string(),
  deactivatedByUserName: z.string(),
  reason: z.string(),
  notes: z.string().optional(),
});

const RebateAgreementWithStructureSchema = z.object({
  id: z.string(),
  original_agreement_id: z.string(),
  version_number: z.number(),
  name: z.string(),
  supplier_id: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  status: z.enum(["draft", "finalized"]),
  notes: z.string().nullable(),
  rebate_payment_terms: z.string().nullable(),
  purchase_payment_terms: z.string().nullable(),
  is_historical: z.boolean(),
  isClosedOut: z.boolean(),
  closureMetadata: ClosureMetadataSchema.nullable(),
  isDeactivated: z.boolean(),
  deactivationMetadata: DeactivationMetadataSchema.nullable(),
  is_pot: z.boolean().nullable(),
  is_pos: z.boolean().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  has_processed_invoices: z.boolean(),
  duplicated_from_agreement: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  supplier: z.object({
    id: z.string(),
    name: z.string(),
  }),
  documents: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      storage_path: z.string(),
      association_type: z.string(),
    }),
  ),
  product_groups: z.array(RebateProductGroupWithIdSchema),
  rules: z.array(RebateRuleWithIdSchema),
});

export const GetAgreementByIdRequestSchema = z.object({
  rebateAgreementId: z.string().uuid("Invalid rebate agreement ID format"),
});

export const GetAgreementByIdResponseSchema = z.object({
  data: RebateAgreementWithStructureSchema,
});

export type GetAgreementByIdRequest = z.infer<
  typeof GetAgreementByIdRequestSchema
>;
export type GetAgreementByIdResponse = z.infer<
  typeof GetAgreementByIdResponseSchema
>;

// ============================================================================
// Deactivate Agreement
// ============================================================================

export const DeactivateAgreementRequestSchema = z.object({
  rebateAgreementId: z.string().uuid("Invalid rebate agreement ID format"),
  reason: z.string().min(1, "Reason is required"),
  notes: z.string().optional(),
});

export const DeactivateAgreementResponseSchema = z.object({
  id: z.string().uuid(),
  isDeactivated: z.boolean(),
  deactivationMetadata: DeactivationMetadataSchema.nullable(),
});

export type DeactivateAgreementRequest = z.infer<
  typeof DeactivateAgreementRequestSchema
>;
export type DeactivateAgreementResponse = z.infer<
  typeof DeactivateAgreementResponseSchema
>;

// ============================================================================
// Reactivate Agreement
// ============================================================================

export const ReactivateAgreementRequestSchema = z.object({
  rebateAgreementId: z.string().uuid("Invalid rebate agreement ID format"),
});

export const ReactivateAgreementResponseSchema = z.object({
  id: z.string().uuid(),
  isDeactivated: z.boolean(),
  deactivationMetadata: DeactivationMetadataSchema.nullable(),
});

export type ReactivateAgreementRequest = z.infer<
  typeof ReactivateAgreementRequestSchema
>;
export type ReactivateAgreementResponse = z.infer<
  typeof ReactivateAgreementResponseSchema
>;

// ============================================================================
// Analyze Version Preview
// ============================================================================

export const AnalyzeVersionPreviewRequestSchema = z.object({
  previousVersionId: z.string().uuid(),
  extractedRules: z.array(z.record(z.string(), z.unknown())),
  restoredRuleIds: z.array(z.string().uuid()),
});

const IncompatibleInvoiceDetailSchema = z.object({
  invoiceId: z.string(),
  invoiceDate: z.string(),
  affectedLineItems: z.number(),
  missingProductGroups: z.array(z.string()),
});

const UnallocatableDetailSchema = z.object({
  paymentId: z.string(),
  paymentDate: z.string(),
  paymentAmount: z.number(),
  affectedRules: z.array(z.string()),
  allocationAmount: z.number(),
});

const ManualAmountDetailSchema = z.object({
  manualAmountId: z.string(),
  divisionName: z.string(),
  amount: z.number(),
  date: z.string(),
  description: z.string().nullable(),
  ruleName: z.string(),
});

export const AnalyzeVersionPreviewResponseSchema: z.ZodType<DeltaAnalysisResult> =
  z.object({
    activeProductGroupsDelta: z.object({
      removed: z.array(z.string()),
      added: z.array(z.string()),
      unchanged: z.array(z.string()),
    }),
    invoiceImpact: z.object({
      totalInvoices: z.number(),
      compatibleInvoices: z.number(),
      incompatibleInvoices: z.number(),
      incompatibleDetails: z.array(IncompatibleInvoiceDetailSchema),
    }),
    paymentImpact: z.object({
      totalPayments: z.number(),
      affectedAllocations: z.number(),
      preservedAllocations: z.number(),
      affectedAmount: z.number(),
      unallocatableDetails: z.array(UnallocatableDetailSchema),
      deletedRules: z.array(z.string()),
    }),
    manualAmountImpact: z.object({
      totalManualAmounts: z.number(),
      affectedManualAmounts: z.number(),
      affectedTotal: z.number(),
      manualAmountDetails: z.array(ManualAmountDetailSchema),
      deletedRules: z.array(z.string()),
    }),
    calculationImpact: z.object({
      currentEstimate: z.number(),
      newEstimate: z.number(),
      delta: z.number(),
    }),
    exclusiveRuleWarning: z
      .object({
        hasExclusiveRules: z.boolean(),
        affectedRules: z.array(
          z.object({
            ruleName: z.string(),
            qualifyingIsExclusive: z.boolean(),
            affectedIsExclusive: z.boolean(),
            currentExcludedGroups: z.array(z.string()),
          }),
        ),
        addedProductGroups: z.array(z.string()),
        warningMessage: z.string(),
      })
      .optional(),
    reportedDataImpact: z
      .object({
        rulesWithReportedData: z.array(
          z.object({
            ruleId: z.string(),
            ruleName: z.string(),
          }),
        ),
      })
      .optional(),
    severity: z.enum(["none", "minor", "major", "destructive"]),
  });

export type AnalyzeVersionPreviewRequest = z.infer<
  typeof AnalyzeVersionPreviewRequestSchema
>;
export type AnalyzeVersionPreviewResponse = z.infer<
  typeof AnalyzeVersionPreviewResponseSchema
>;

// ============================================================================
// Contract Definitions
// ============================================================================

export const agreementsContracts = {
  getBySupplier: oc
    .input(GetAgreementsBySupplierRequestSchema)
    .output(GetAgreementsBySupplierResponseSchema)
    .route({
      method: "GET",
      summary: "List a supplier's rebate agreements",
      description:
        "Lists the latest version of each rebate agreement for a supplier, with name, date range, and draft or finalized status.",
    }),

  getById: oc
    .input(GetAgreementByIdRequestSchema)
    .output(GetAgreementByIdResponseSchema)
    .route({
      method: "GET",
      summary: "Get a rebate agreement",
      description:
        "Returns a rebate agreement with its supplier, rules, tiers, product groups, divisions, documents, and closure or deactivation details.",
    }),

  deactivate: oc
    .input(DeactivateAgreementRequestSchema)
    .output(DeactivateAgreementResponseSchema)
    .route({
      method: "POST",
      summary: "Deactivate a rebate agreement",
      description:
        "Deactivates a rebate agreement with a reason and notes, removes its estimates and predictions across versions, and zeroes its reported earnings from the current month.",
    }),

  reactivate: oc
    .input(ReactivateAgreementRequestSchema)
    .output(ReactivateAgreementResponseSchema)
    .route({
      method: "POST",
      summary: "Reactivate a rebate agreement",
      description:
        "Reactivates a deactivated rebate agreement and clears its deactivation details.",
    }),

  analyzeVersionPreview: oc
    .input(AnalyzeVersionPreviewRequestSchema)
    .output(AnalyzeVersionPreviewResponseSchema)
    .route({
      method: "POST",
      summary: "Preview impact of a new agreement version",
      description:
        "Compares proposed rules against the previous agreement version and reports the effect on product groups, transactions, payment allocations, manual amounts, and estimated rebate, with a severity rating. Saves nothing.",
    }),
} as const;
